import { FunctionsHttpError } from "@supabase/supabase-js";

// supabase.functions.invoke() throws a FunctionsHttpError on any non-2xx
// response, whose .message is the opaque "Edge Function returned a non-2xx
// status code". The real payload (our functions return { error, details }) sits
// unread on error.context (a Response). This pulls the meaningful message out.
export async function readEdgeError(
  error: unknown,
  fallback = "Something went wrong",
): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const raw = await error.context.text();
      try {
        const body = JSON.parse(raw);
        if (body?.error) {
          const detail = body?.detail || body?.details?.message || body?.message;
          return detail && detail !== body.error ? `${body.error}: ${detail}` : body.error;
        }
      } catch {
        if (raw) return raw;
      }
    } catch {
      // body unreadable. Fall through to the generic message
    }
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}

// Same unwrapping, but returns only the human-readable `detail` when the
// function sent one. For owner-facing flows whose functions write their
// details as complete sentences, where an `error_code:` prefix is noise.
export async function readEdgeDetail(
  error: unknown,
  fallback = "Something went wrong",
): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = JSON.parse(await error.context.clone().text());
      if (typeof body?.detail === "string" && body.detail) return body.detail;
    } catch {
      // not JSON. Fall through to the code-prefixed message
    }
  }
  return readEdgeError(error, fallback);
}
